import { Injector, Injectable, inject, signal } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@app/typedef/define/user/User';
import { UserCodec } from '@app/typedef/codec/user/UserCodec';
import { UserOrganization } from '@app/typedef/define/user/UserOrganization';
import { UserSettings } from '@app/typedef/define/user/UserSettings';
import { UserSettingsCodec } from '@app/typedef/codec/user/UserSettingsCodec';
import { UserSettingsService } from './user.settings.service';
import { UserOrganizationService } from './user.organization.service';

/**
 * 当前账号。
 *
 * <p>登录态存在 `localStorage` 的 `user` 键里，刷新页面后从那里恢复。</p>
 *
 * <p>除了「谁登录了」，这里还管**组织上下文**：当前组织是前端保有的概念（account 后端没有服务端
 * 「当前组织」，每个组织接口都要显式传组织代码）。`organization` 信号与 `organizationId`/`userSettings`
 * 两个 localStorage 键负责把「上次选的哪个组织」跨刷新捞回来；点表数据本身仍归账号个人，
 * 组织只作为后续要接组织化数据前的上下文存在。</p>
 */
@Injectable({ providedIn: 'root' })
export class AccountService {

  public login = signal(false);
  public user = signal<User>(new User());
  public organization = signal<UserOrganization>(new UserOrganization());
  public userSettings = signal<UserSettings>(new UserSettings());

  // 这两个 service 都吃 HttpClient，而 HttpClient 的拦截器链里有 JwtInterceptor 会
  // `inject(AccountService)` —— 所以在构造期同步在某字段上 `inject(...)` 它们会成环 NG0200
  // （与下方 t() 里 TranslateService 同一个坑）。改成经 Injector 按需取，而且只有构造完成
  // 之后（刷新上下文已用 queueMicrotask 延后）才真正解析，环自然就断了。
  private readonly msg = inject(NzMessageService);
  private readonly injector = inject(Injector);

  private get settingsService(): UserSettingsService {
    return this.injector.get(UserSettingsService);
  }

  private get organizations(): UserOrganizationService {
    return this.injector.get(UserOrganizationService);
  }

  constructor() {
    const saved = localStorage.getItem('user') || null;
    if (saved !== null) {
      this.user.set(UserCodec.decode(JSON.parse(saved)));
      this.login.set(true);
      // 刷新页面后把设置 / 上次选的组织一并捞回来。延到微任务（下一帧渲染前）是在躲上面的
      // 构造期成环：此刻 AccountService 还没构造完，同步取 settingsService / organizations
      // 就会把 HttpClient 带进来、又被 JwtInterceptor 引回自己。推后一轮再取就安全，视觉无感。
      queueMicrotask(() => this.refreshUserContext());
    }
  }

  setUser(user: User) {
    localStorage.setItem('user', UserCodec.encode(user));
    this.user.set(user);
    this.login.set(true);
    this.refreshUserContext();
  }

  /**
   * 清登录态。只删跟账号有关的几个键，**不能用 `localStorage.clear()`** —— 后者会顺手把主题
   * （`app_theme`）、侧栏收起状态（`main.sidebarCollapsed`）等一起抹掉。组织也得复位：
   * 它是「当前账号」的一部分，留着的话换个账号登录后它还发着上一个账号的组织 id。
   */
  clear() {
    localStorage.removeItem('user');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('userSettings');
    this.login.set(false);
    this.user.set(new User());
    this.organization.set(new UserOrganization());
    this.userSettings.set(new UserSettings());
  }

  /** 切换当前组织：非空选中写 localStorage，空对象（取消选中）删掉那条记录；信号跟着 set。 */
  setOrganization(organization: UserOrganization) {
    if (this.organization().id === organization.id) {
      return;
    }

    if (organization.id) {
      localStorage.setItem('organizationId', organization.id);
    } else {
      localStorage.removeItem('organizationId');
    }

    this.organization.set(organization);
  }

  /** 该组织是否就是当前选中的组织（列表卡片上的勾选标记靠它） */
  isCurrentOrganization(organization: UserOrganization): boolean {
    return this.organization().id === organization.id;
  }

  /**
   * 个人组织：列表里永远排第一张的**虚拟**组织，不落库、后端没有它。
   *
   * <p>`id` 留空 —— 空 id 正是当前组织的天然「未选中」形态
   * （`organization` 信号默认值就是 `new UserOrganization()`），于是 `isCurrentOrganization` 在
   * 空选中态对它恒真；点它走 `setOrganization` 的空 id 分支删掉 localStorage。名称就是用户名：
   * 组织功能没开时顶栏/卡片显示的还是他本人，而不是跳出一个「个人」的生造词。</p>
   */
  personalOrganization(): UserOrganization {
    const personal = new UserOrganization();
    personal.name = this.user().name;
    personal.personal = true;
    return personal;
  }

  /**
   * 登录后拉取当前用户的设置，再恢复上次选中的组织。先同步读缓存立即渲染，再刷新服务器值
   * （避免进组织页时开关先闪一下默认值）。
   *
   * 在 `setUser` 与构造函数的 `login=true` 分支里调用（登录 / 刷新各一次）。
   */
  private refreshUserContext(): void {
    if (!this.login()) {
      return;
    }

    const cached = localStorage.getItem('userSettings');
    if (cached !== null) {
      this.userSettings.set(UserSettingsCodec.decode(JSON.parse(cached)));
    }

    this.settingsService.getSettings().subscribe({
      next: (settings) => {
        this.userSettings.set(settings);
        localStorage.setItem('userSettings', UserSettingsCodec.encode(settings));
        this.restoreOrganization();
      },
      error: (error) => this.msg.warning(error),
    });
  }

  /**
   * 更新当前用户设置：先乐观更新本地，失败则拉回服务器上的值回滚。
   * 只有**关掉**组织功能才清空已选组织（组织从界面上消失，留着选中态没意义）；打开时不动。
   */
  updateSettings(settings: UserSettings) {
    this.userSettings.set(settings);
    localStorage.setItem('userSettings', UserSettingsCodec.encode(settings));

    this.settingsService.updateSettings(settings).subscribe({
      next: () => {
        this.msg.success(
          settings.organizationEnabled
            ? this.t('组织功能已启用')
            : this.t('组织功能已禁用'),
        );
        if (!settings.organizationEnabled) {
          this.clearCurrentOrganization();
        }
        this.refreshUserContext();
      },
      error: (error) => {
        this.msg.warning(error);
        // 回滚：重拉服务端值（离线时不动 userSettings，仍留乐观值）
        this.settingsService.getSettings().subscribe({
          next: (fresh) => {
            this.userSettings.set(fresh);
            localStorage.setItem('userSettings', UserSettingsCodec.encode(fresh));
          },
          error: () => undefined,
        });
      },
    });
  }

  /**
   * 翻译一条词条。
   *
   * <p>**不能**在构造时 `inject(TranslateService)`：本服务被 `JwtInterceptor` 依赖，而
   * ngx-translate 的 http-loader 又要用 HttpClient（带这组拦截器），于是
   * `AccountService → TranslateService → HttpClient → JwtInterceptor → AccountService` 成环，
   * 启动即 NG0200。改成经 `Injector` **按需**取 —— 只在真正要弹成功消息时（早已过了 bootstrap）
   * 才解析 TranslateService，环就断了。矩阵那版压根不弹成功消息，天然躲开这个问题。</p>
   */
  private t(key: string): string {
    return this.injector.get(TranslateService).instant(key);
  }

  /**
   * 恢复上次选中的组织。本地那个 id 可能已失效（组织被删 / 自己被移出），拿它去查就是一路
   * not-found，却一直躺在本地每次进来都重来一遍 —— 所以先跟服务端组织列表核一遍：对得上才用，
   * 对不上就地清掉。请求本身失败则**不清**（那多半只是网络抖动，不该顺手把用户选择抹掉）。
   */
  private restoreOrganization(): void {
    const saved = localStorage.getItem('organizationId') || null;
    if (saved === null || !this.userSettings().organizationEnabled) {
      return;
    }

    this.organizations.getOrganizations().subscribe({
      next: (organizations) => {
        const found = organizations.find((o) => o.id === saved);
        if (found) {
          this.organization.set(found);
        } else {
          localStorage.removeItem('organizationId');
        }
      },
      error: () => undefined,
    });
  }

  /** 清空已选组织：删除本地记录并复位信号（关掉组织功能时调用）。 */
  private clearCurrentOrganization() {
    localStorage.removeItem('organizationId');
    this.organization.set(new UserOrganization());
  }
}